import { useMemo } from "react";
import Fuse from "fuse.js";

interface FuzzySearchOptions<T> {
  items: T[];
  searchQuery: string;
  keys: string[]; // Paths to search in (e.g., ['title', 'customer.name'])
  threshold?: number; // 0.0 = perfect match, 1.0 = match anything
  includeScore?: boolean;
  limit?: number;
}

/**
 * Simple fuzzy search hook using Fuse.js
 * Supports typos and partial matches for small datasets
 * For large datasets or complex product search, use backend search instead
 */
export function useFuzzySearch<T>(
  options: FuzzySearchOptions<T> & { includeScore: true },
): { item: T; score: number }[];
export function useFuzzySearch<T>(
  options: FuzzySearchOptions<T> & { includeScore?: false },
): T[];
export function useFuzzySearch<T>({
  items,
  searchQuery,
  keys,
  threshold = 0.4, // 0.0 = perfect match, 1.0 = match anything
  includeScore = false,
  limit,
}: FuzzySearchOptions<T>): T[] | { item: T; score: number }[] {
  // Create Fuse instance with simple configuration
  const fuse = useMemo(() => {
    if (!items.length || !keys.length) return null;

    return new Fuse(items, {
      keys: keys,
      threshold: threshold,
      distance: 100,
      minMatchCharLength: 1,
      includeScore: true,
      ignoreLocation: true,
      findAllMatches: true,
      useExtendedSearch: false,
    });
  }, [items, keys, threshold]);

  return useMemo(() => {
    if (!fuse || !searchQuery.trim()) {
      const results = limit ? items.slice(0, limit) : items;
      return includeScore
        ? results.map((item) => ({ item, score: 1 }))
        : results;
    }

    const query = searchQuery.trim();
    const results = fuse.search(query, { limit: limit || 50 });

    if (includeScore) {
      return results.map((result) => ({
        item: result.item,
        score: result.score || 0,
      }));
    }

    return results.map((result) => result.item);
  }, [fuse, searchQuery, limit, includeScore, items]);
}

/**
 * Enhanced filtered list hook with fuzzy search
 * Useful for filtering lists with status tabs
 */
interface UseEnhancedFilteredListOptions<T> {
  items: T[];
  activeTab: string;
  searchQuery: string;
  statusKey?: keyof T;
  searchKeys: string[]; // Fuse.js key paths
  threshold?: number;
  customSearchFn?: (item: T, query: string) => boolean;
}

export function useEnhancedFilteredList<T>({
  items,
  activeTab,
  searchQuery,
  statusKey = "status" as keyof T,
  searchKeys,
  threshold = 0.4,
  customSearchFn,
}: UseEnhancedFilteredListOptions<T>): T[] {
  // First filter by status tab
  const statusFiltered = useMemo(() => {
    if (!items || activeTab === "all") return items;

    return items.filter((item: T) => {
      const itemStatus = item[statusKey];
      return itemStatus === activeTab;
    });
  }, [items, activeTab, statusKey]);

  // Then apply fuzzy search
  const searchResults = useFuzzySearch({
    items: statusFiltered,
    searchQuery,
    keys: searchKeys,
    threshold,
  });

  // Apply custom search function if no fuzzy results and custom function exists
  return useMemo(() => {
    if (!searchQuery.trim()) return statusFiltered;

    // If fuzzy search found results, use them
    if (searchResults.length > 0) return searchResults;

    // Fallback to custom search function
    if (customSearchFn) {
      const query = searchQuery.toLowerCase();
      return statusFiltered.filter((item: T) => customSearchFn(item, query));
    }

    return searchResults; // Empty array if no matches
  }, [statusFiltered, searchResults, searchQuery, customSearchFn]) as T[];
}
